import {isEqual, uniqWith} from 'es-toolkit'
import {get} from 'es-toolkit/compat'
import {FormikValues} from 'formik'
import * as Yup from 'yup'

import {Nullable} from '../../types'

export type MixedValue<Type> = {mixed: true; values: Type[]}
export type PossiblyMixedValue<Type> = Type | MixedValue<Type>

// TODO do we still need this?
// TODO can we get rid of close small icon?
export const unlockMixedValuesLabel = 'Edit mixed values'

export const isMixedValue = <Type>(value: PossiblyMixedValue<Type>): value is MixedValue<Type> =>
  !!get(value, 'mixed', false)

export const toNumberValue = (value: Nullable<string>) => (value ? parseFloat(value) : null)

export const toIntegerValue = (value: Nullable<string>) => (!value ? null : parseInt(value, 10))

export const toStringValue = (value: Nullable<number>) => (value === null ? '' : `${value}`)

export const percentageStringToFactor = (percentage: Nullable<string>): Nullable<number> =>
  percentage ? parseFloat(percentage) / 100 + 1 : null

export const percentageStringToFactorMax1 = (percentage: Nullable<string>): Nullable<number> =>
  percentage ? parseFloat(percentage) / 100 : null

export const factorToPercentageString = (factor: Nullable<number>): string =>
  factor ? `${Number((factor * 100 - 100).toFixed(2))}` : ''

export const toMixedValues = <V>(values: V[]): MixedValue<V> => ({mixed: true, values})

export const toPossiblyMixedValue = <T, V>(
  values: V[],
  formatter: (value: V) => T
): PossiblyMixedValue<T> => {
  const isValuesMixed = uniqWith(values, isEqual).length > 1
  return isValuesMixed ? toMixedValues(values.map(formatter)) : formatter(values[0])
}

export const toTouchedFormData = <FormData extends FormikValues>(
  values: FormData,
  initialValues: FormData
) => {
  const touchedValues: Partial<FormData> = {}
  Object.keys(values).forEach(key => {
    const value = values[key as keyof FormData]

    if (value !== initialValues[key as keyof FormData]) {
      touchedValues[key as keyof FormData] = value
    }
  })

  return touchedValues
}

export const toValidationSchema = <C>(
  validationSchema: {
    [key: string]: Yup.ISchema<unknown, C, Yup.Flags, unknown>
  },
  excludes?: [string, string][]
): Yup.AnyObjectSchema => Yup.object().shape(validationSchema, excludes)

// Asynchronously evaluate each field value in the validation schema with a `Lazy` instance and determine
// if it's a mixed value (ignore validation) or not (apply the initial validation from schema).
export const toMixedValidationSchema = (
  validationSchema: {
    [key: string]: Yup.SchemaLazyDescription | Yup.AnySchema
  },
  excludes?: [string, string][]
): Yup.AnyObjectSchema => {
  const mixedValidationSchema = Object.keys(validationSchema).reduce(
    (accumulator, currentValue) => ({
      ...accumulator,
      [currentValue]: Yup.lazy((value: PossiblyMixedValue<unknown>) => {
        if (isMixedValue(value)) {
          return Yup.object().notRequired()
        }

        // Lazy only takes Schemas as children according to it's type - although we allow to nest Lazy instances here.
        return validationSchema[currentValue] as Yup.AnySchema
      }),
    }),
    {}
  )

  return Yup.object().shape(mixedValidationSchema, excludes)
}
